import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useWatch } from 'react-hook-form';
import { useReactToPrint } from 'react-to-print';
import { EpInfoCard } from '../../components';
import { EpShareEmailModal } from '../../components/EpShareEmailModal';
import { EpButton } from '../../components/HookFormComponents/components';
import { RECAPTCHA_SEND_EMAIL_NOTIFIES } from '../../constants/recaptchaConstants';
import {
  ROUTE_HOMEPAGE,
  ROUTE_PHARMACY_HOMEPAGE,
  ROUTE_PUBLIC_HOMEPAGE,
  ROUTE_PUBLIC_PHARMACY_HOMEPAGE,
} from '../../constants/urlConstants';
import { useTranslation as t } from '../../hooks/useTranslation';
import { globalizationService } from '../../services/globalizationService';
import { notificationService } from '../../services/notificationService';
import { useHospitalStore, useNotificationStore } from '../../stores';
import styles from './EpConfirmationTemplate.module.scss';
import {
  IAccessCodeStateProps,
  NotificationContent,
  NotificationContentClinicalPharmacy,
  NotificationSuccessSend,
  NotificationSuccessSendClinicalPharmacy,
} from './EpConfirmationTemplateComponents';

export default function EpConfirmationTemplate({
  tag,
  template = 'default',
}: {
  tag: string;
  template?:
  | 'clinical-pharmacy'
  | 'default'
  | 'PS/web/src/components/HookFormComponents/Forms/ClinicalPharmacy/Steps/steps.module.scss';
}) {
  const componentRef = useRef(null);
  const anonymous = useWatch({ name: 'anonymous' });
  const [access, setAccess] = useState<IAccessCodeStateProps>({
    code: null,
    password: null,
    clinicalPharmacyId: null,
  });
  const [email, setEmail] = useState('');
  const { hospitalInfo } = useHospitalStore((state) => state);
  const { accessCode, clearAccessCode } = useNotificationStore(
    (state) => state,
  );
  const { translations } = globalizationService.getTranslationsByTag(tag);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { mutate: fetchSendMail } = notificationService.createEmailNotifier();
  const pathname = usePathname();

  const loggedRoute = pathname.includes('PSNotificationPharmacyInterventions')
    ? ROUTE_PHARMACY_HOMEPAGE
    : ROUTE_HOMEPAGE;

  const notLoggedRoute = pathname.includes('PSNotificationPharmacyInterventions')
    ? ROUTE_PUBLIC_PHARMACY_HOMEPAGE
    : ROUTE_PUBLIC_HOMEPAGE;

  const buttonHomeRedirect = pathname.includes('/Public')
    ? notLoggedRoute
    : loggedRoute;

  useEffect(() => {
    if (accessCode) {
      setAccess({
        code: accessCode?.code as string,
        clinicalPharmacyId: accessCode?.clinicalPharmacyId as string,
        password: accessCode?.password as string,
      });
      clearAccessCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessCode]);

  const takeNotificationIdTextCopy = () => {
    const codeText = `${t(
      translations,
      'Your notification number is',
    )} ${access?.code}`;
    const passwordText = access?.password
      ? `, ${t(translations, 'Access password')} ${access?.password}`
      : '';
    navigator.clipboard.writeText(`${codeText} ${passwordText}`);
  };

  const takeNotificationPrint = () => {
    handlePrint();
  };

  const takeNotificationSendMail = async () => {
    if (!executeRecaptcha) return;
    const recaptchaToken = await executeRecaptcha(
      RECAPTCHA_SEND_EMAIL_NOTIFIES,
    );
    if (recaptchaToken) {
      fetchSendMail({
        // tenantId: hospitalInfo?.TenantId,
        // networkId: hospitalInfo?.NetworkId,
        hospitalId: hospitalInfo?.HospitalId,
        email,
        notificationId: access?.code,
        password: access?.password,
        recaptchaToken,
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const notificationTemplate: any = {
    default: {
      notification: <NotificationSuccessSend translations={translations} />,
      content: (
        <NotificationContent
          code={access?.code as string}
          password={access?.password as string}
          takeNotificationIdTextCopy={takeNotificationIdTextCopy}
          takeNotificationPrint={takeNotificationPrint}
          translations={translations}
        />
      ),
    },
    'clinical-pharmacy': {
      notification: (
        <NotificationSuccessSendClinicalPharmacy
          code={access?.clinicalPharmacyId as string}
          translations={translations}
        />
      ),
      content: (
        <NotificationContentClinicalPharmacy
          code={access?.code as string}
          password={access?.password as string}
          takeNotificationIdTextCopy={takeNotificationIdTextCopy}
          takeNotificationPrint={takeNotificationPrint}
          translations={translations}
        />
      ),
    },
  };

  const reactToPrintContent = useCallback(() => {
    return componentRef;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentRef]);

  const handlePrint = useReactToPrint({
    content: () => reactToPrintContent()?.current,
    removeAfterPrint: false,
    print: async (target: HTMLIFrameElement) => {
      target.setAttribute('sandbox', 'allow-modals allow-forms allow-popups allow-scripts allow-same-origin');
      if (target?.contentWindow?.print) {
        target.contentWindow.print();
      }
    },
  });

  return (
    <div className={styles['content']}>
      <div className={styles['not-printable']}>
        {notificationTemplate[template].notification}
      </div>

      {anonymous && (
        <div className={clsx(styles['not-printable'], styles['content__mt'])}>
          <EpInfoCard
            customHeaderType={'danger'}
            title={t(translations, 'This notification was sent anonymously.')}
            type={'filled'}
          >
            <p className={styles['content__txt']}>
              {t(
                translations,
                'If you lose your notification number and password, you will not be able to access and follow this notification in the future.',
              )}
            </p>
          </EpInfoCard>
        </div>
      )}

      <div ref={componentRef}>{notificationTemplate[template].content}</div>

      <div
        className={clsx(
          styles['content__btn'],
          styles['content__btn--back'],
          styles['not-printable'],
        )}
      >
        <Link
          href={{
            pathname: buttonHomeRedirect,
            query: {
              ...hospitalInfo,
            },
          }}
        >
          <EpButton
            buttonSize="md"
            buttonText={t(translations, 'Back to homepage')}
            buttonType="primary-outline"
            buttonLeftIconName="SolidLongArrowAltLeft"
          />
        </Link>
      </div>

      <EpShareEmailModal
        translations={translations}
        sendMailFunc={() => takeNotificationSendMail?.()}
        setEmail={(value) => setEmail(value)}
      />
    </div>
  );
}
